import { ChevronDown, Maximize2, RectangleHorizontal, RectangleVertical, Square } from "lucide-react"
import { useRef, useState } from "react"
import { createPortal } from "react-dom"
import { RemoveScroll } from "react-remove-scroll"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import {
  DEFAULT_FRAME_FORMAT,
  FRAME_FORMATS,
  frameFormatLabel,
  frameFormatShort,
  normalizeFrameFormat,
  type FrameFormat,
} from "@/lib/frame-format"
import { UI_ACCENT_FILLED } from "@/lib/ui-selection"
import { cn } from "@/lib/utils"

export type FrameFormatSelectProps = {
  value: string
  onChange: (value: FrameFormat) => void
  options?: FrameFormat[]
  disabled?: boolean
  className?: string
  size?: "sm" | "md"
}

export function FrameFormatIcon({ format, className }: { format: string; className?: string }) {
  return formatIcon(normalizeFrameFormat(format), className ?? "h-3.5 w-3.5")
}

function formatIcon(format: FrameFormat, className: string) {
  switch (format) {
    case "16:9":
      return <RectangleHorizontal className={className} />
    case "1:1":
      return <Square className={className} />
    case "21:9":
      return <RectangleHorizontal className={cn(className, "scale-x-125")} />
    case "3:4":
      return <RectangleVertical className={className} />
    case "4:3":
      return <RectangleHorizontal className={className} />
    default:
      return <RectangleVertical className={className} />
  }
}

export function FrameFormatSelect({
  value,
  onChange,
  options = FRAME_FORMATS,
  disabled = false,
  className,
  size = "sm",
}: FrameFormatSelectProps) {
  const normalizedValue = normalizeFrameFormat(value)
  const available = options.length > 0 ? options : FRAME_FORMATS
  const iconClass = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"
  const buttonClass = size === "sm" ? "h-8 gap-1 px-2 text-[11px]" : "h-10 gap-1.5 px-2.5 text-xs"

  return (
    <div
      className={cn(
        "inline-flex rounded-lg border border-ui-border bg-ui-surface-sunken p-0.5",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      role="group"
      aria-label={frameFormatLabel(normalizedValue)}
    >
      {available.map((format) => {
        const selected = normalizedValue === format
        return (
          <button
            key={format}
            type="button"
            disabled={disabled}
            aria-pressed={selected}
            onClick={() => onChange(format)}
            className={cn(
              "inline-flex items-center rounded-md font-medium transition-colors",
              buttonClass,
              selected
                ? UI_ACCENT_FILLED
                : "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary",
            )}
          >
            {formatIcon(format, iconClass)}
            <span className="tabular-nums">{frameFormatShort(format)}</span>
          </button>
        )
      })}
    </div>
  )
}

export function aspectRatiosToFrameFormats(ratios: string[]): FrameFormat[] {
  return FRAME_FORMATS.filter((format) => ratios.includes(format))
}

export type FrameFormatDropdownProps = {
  value: string
  onChange: (value: FrameFormat | "fill") => void
  options?: FrameFormat[]
  /** When set, prepends a "fill" option (Quick Markup canvas layout). */
  fillOption?: { label: string }
  disabled?: boolean
  className?: string
  dropdownDirection?: "up" | "down"
  /** Portal to document.body so the list can extend past dialog edges. */
  portalToBody?: boolean
  ariaLabel?: string
}

export function FrameFormatDropdown({
  value,
  onChange,
  options = FRAME_FORMATS,
  fillOption,
  disabled = false,
  className,
  dropdownDirection = "down",
  portalToBody = false,
  ariaLabel,
}: FrameFormatDropdownProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)
  const isFill = Boolean(fillOption) && value === "fill"
  const normalizedValue = isFill ? null : normalizeFrameFormat(value)
  const available = options.length > 0 ? options : FRAME_FORMATS
  const iconClass = "h-3.5 w-3.5 shrink-0"

  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    disabled,
    direction: dropdownDirection,
    align: "left",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    portalToBody,
    resolveWidth: (rect) => ({ minWidth: rect.width }),
    onOutsideClick: () => setOpen(false),
  })

  const triggerLabel = isFill
    ? (fillOption?.label ?? "Fill")
    : frameFormatShort(normalizedValue ?? DEFAULT_FRAME_FORMAT)
  const triggerAria =
    ariaLabel ?? (isFill ? (fillOption?.label ?? "Fill") : frameFormatLabel(normalizedValue ?? DEFAULT_FRAME_FORMAT))

  const menu =
    open && !disabled && menuStyle && portalRoot ? (
      <ul
        ref={menuRef}
        role="listbox"
        data-panel-wheel-isolate
        aria-label={triggerAria}
        style={menuStyle}
        className="overflow-auto rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-xl shadow-black/40"
      >
        {fillOption ? (
          <li
            role="option"
            aria-selected={isFill}
            aria-label={fillOption.label}
            onMouseDown={(e) => {
              e.preventDefault()
              onChange("fill")
              setOpen(false)
            }}
            className={cn(
              "flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs transition-colors hover:bg-ui-accent/25",
              isFill ? "bg-ui-accent-soft text-ui-accent-text" : "text-ui-text-primary",
            )}
          >
            <Maximize2 className={iconClass} />
            <span className="min-w-0 flex-1 truncate">{fillOption.label}</span>
          </li>
        ) : null}
        {available.map((format) => (
          <li
            key={format}
            role="option"
            aria-selected={!isFill && normalizedValue === format}
            aria-label={frameFormatLabel(format)}
            onMouseDown={(e) => {
              e.preventDefault()
              onChange(format)
              setOpen(false)
            }}
            className={cn(
              "flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs transition-colors hover:bg-ui-accent/25",
              !isFill && normalizedValue === format ? "bg-ui-accent-soft text-ui-accent-text" : "text-ui-text-primary",
            )}
          >
            {formatIcon(format, iconClass)}
            <span className="tabular-nums">{frameFormatShort(format)}</span>
          </li>
        ))}
      </ul>
    ) : null

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={triggerAria}
        onClick={() => {
          if (disabled) return
          setOpen((prev) => !prev)
        }}
        className={cn(
          "inline-flex h-8 w-full min-w-[5.5rem] items-center gap-1 rounded-lg border border-ui-border bg-ui-surface-sunken py-0 pr-2 pl-2 text-xs leading-none text-ui-text-primary",
          "focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none",
          disabled && "pointer-events-none opacity-50",
        )}
      >
        {isFill ? <Maximize2 className={iconClass} /> : formatIcon(normalizedValue ?? DEFAULT_FRAME_FORMAT, iconClass)}
        <span className={cn("min-w-0 flex-1 truncate text-left", !isFill && "tabular-nums")}>{triggerLabel}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-ui-text-muted", open && "rotate-180")} />
      </button>
      {menu && portalRoot
        ? createPortal(
            portalToBody ? (
              <RemoveScroll enabled={open} allowPinchZoom removeScrollBar={false}>
                {menu}
              </RemoveScroll>
            ) : (
              menu
            ),
            portalRoot,
          )
        : null}
    </div>
  )
}
